// sonarignore
import { Flex, Text } from '@chakra-ui/react';
import { ColumnDef, Row } from '@tanstack/react-table';

import {
  CheckboxHeader,
  formatDate,
  IncMaCoHeader,
  TableCell,
} from 'innovate/utils/PreLaunch/PreLaunchHelper';
import { ModalType, ScenarioType, SectionInfo } from 'innovate/utils/types';

export const clientId =
  import.meta.env.VITE_ENV === 'prod'
    ? (import.meta.env.VITE_CLIENT_ID_PROD ?? '')
    : (import.meta.env.VITE_CLIENT_ID ?? '');
export const redirectUri = import.meta.env.VITE_REDIRECT_URI ?? '';
export const fixed = import.meta.env.VITE_API_URL ?? '';
export const applicationId = import.meta.env.VITE_APPLICATION_ID ?? '';
export const clientToken = import.meta.env.VITE_CLIENT_TOKEN ?? '';
export const site = import.meta.env.VITE_SITE ?? '';
export const service = import.meta.env.VITE_SERVICE ?? 'innox-fe';
export const environment = import.meta.env.VITE_ENV ?? 'prod';

type CellComponent = (props: { row: Row<ScenarioType> }) => JSX.Element;

export const categoryDistributionRSA = { BEER: 6.11, 'BEYOND BEER': 6.15 };
export const categoryDistributionBR = {
  BEER: 4.9,
  'BEYOND BEER': 22.8,
  NAB: 21.8,
  'NON ALC BEER': 5.9,
};

// ----------------------------------
// Common Text Cell Helper (DRY)
// ----------------------------------

const TextCell = () => {
  const Cell = (props: { getValue: () => string }) => (
    <Text whiteSpace="normal" fontFamily="Avantt">
      {props.getValue()}
    </Text>
  );
  Cell.displayName = 'TextCell';
  return Cell;
};

// ----------------------------------
// DRY Column Builder
// ----------------------------------
const C = (header: string, accessorKey: string) => ({
  header: () => (
    <Text textAlign="center" fontFamily="Avantt" fontSize="12px" fontWeight="600" lineHeight="1.3">
      {header}
    </Text>
  ),
  accessorKey,
  cell: TextCell(),
});

// ----------------------------------
// Growth Rate (cleaned duplication)
// ----------------------------------
const G = (header: string, key: string) => ({
  header: () => (
    <Text
      whiteSpace="pre-line"
      textAlign="center"
      fontFamily="Avantt"
      fontSize="12px"
      fontWeight="600"
      lineHeight="1.3"
    >
      {header}
    </Text>
  ),
  accessorKey: key,
  cell: TextCell(),
});

export const pptData = [
  {
    Zone: 'Africa',
    Country: 'South Africa',
    Financials_source: 'UMAMI',
    Refresh_year: 2024,
    Refresh_month: 'August',
  },
  {
    Zone: 'SAZ',
    Country: 'Brazil',
    Financials_source: 'CUBO',
    Refresh_year: 2024,
    Refresh_month: 'August',
  },
  {
    Zone: 'EUR',
    Country: 'UK',
    Financials_source: 'CPT',
    Refresh_year: 2024,
    Refresh_month: 'August',
  },
  {
    Zone: 'MAZ',
    Country: 'Mex',
    Financials_source: 'CIG',
    Refresh_year: 2024,
    Refresh_month: 'December',
  },
  {
    Zone: 'MAZ',
    Country: 'COL',
    Financials_source: '',
    Refresh_year: 2026,
    Refresh_month: '',
  },
  {
    Zone: 'MAZ',
    Country: 'PER',
    Financials_source: '',
    Refresh_year: 2026,
    Refresh_month: '',
  },
  {
    Zone: 'MAZ',
    Country: 'ECU',
    Financials_source: '',
    Refresh_year: 2026,
    Refresh_month: '',
  },
  {
    Zone: 'MAZ',
    Country: 'DR',
    Financials_source: '',
    Refresh_year: 2026,
    Refresh_month: '',
  },
  {
    Zone: 'APAC',
    Country: 'China',
    Financials_source: '',
    Refresh_year: 2026,
    Refresh_month: 'March',
  },
];

export const innoExploreOptions = [
  { value: 'Brazil', label: 'Brazil', type: 'Innobrew' },
  { value: 'South Africa', label: 'South Africa', type: 'Innobrew' },
  { value: 'UK-Brew', label: 'UK', type: 'Innobrew' },
  { value: 'Mex-Brew', label: 'Mexico', type: 'Innobrew' },
];

export const filterByCountry = (country: string) => {
  const key = country.toLowerCase();
  // Map both chn and cnn to china
  const searchKey = key === 'chn' || key === 'cnn' ? 'china' : key;
  return pptData.find((item) => item.Country.toLowerCase() === searchKey);
};

export const chakraStyles = (customWidth: string, customFont: string, customAlign: string) => ({
  control: (baseStyles: any, state: any) => ({
    ...baseStyles,
    width: customWidth,
    fontSize: customFont,
    fontWeight: '600',
    textAlign: customAlign,
    color: 'black',
    bg: 'white',
    padding: '0 16px 0',
    backgroundColor: 'white !important',
    // border: "none",
    borderColor: state.isFocused ? 'primaryBlack !important' : 'border !important',
    borderRadius: '8px',
    boxShadow: 'none !important',
  }),
  placeholder: (baseStyles: any) => ({
    ...baseStyles,
    color: '#1E1E1ECC',
  }),
  valueContainer: (baseStyles: any) => ({
    ...baseStyles,
    px: '0',
  }),
  multiValue: (baseStyles: any) => ({
    ...baseStyles,
    bg: 'border',
    fontSize: '12px',
  }),
  dropdownIndicator: (baseStyles: any) => ({
    ...baseStyles,
    px: '200px',
    bg: 'green',
    cursor: 'pointer',
  }),
  option: (baseStyles: any, state: any) => ({
    ...baseStyles,
    color: 'textBlack !important',
    fontSize: '12px',
    background: state.isFocused ? 'border !important' : 'none',
  }),
});

interface GetColumnsParams {
  isShared: boolean;
  cellRenderers: {
    editName: CellComponent;
    preCheckBox: CellComponent;
    actionbuttons: CellComponent;
    pinnedView: CellComponent;
  };
  headerRenderers: {
    pinnedHeader: () => JSX.Element;
    scenarioNameHeader?: () => JSX.Element;
    dateHeader?: () => JSX.Element;
    sharedOnHeader?: () => JSX.Element;
    countryHeader?: () => JSX.Element;
    brandHeader?: () => JSX.Element;
    totalVolumeHeader?: () => JSX.Element;
    incrementalVolumeHeader?: () => JSX.Element;
    incrementalMaCoHeader?: () => JSX.Element;
  };
}

/**
 * Generates table columns for PreLaunch scenarios
 *
 * IMPORTANT: Column order affects CSS selectors in PreLaunchPage.css
 * - nth-last-child(2) targets Incremental MaCo (2nd from end)
 * - nth-last-child(3) targets Incremental Volume (3rd from end)
 * If you reorder columns, update PreLaunchPage.css accordingly
 */
export const getColumns = ({
  isShared,
  cellRenderers,
  headerRenderers,
}: GetColumnsParams): ColumnDef<ScenarioType>[] => {
  const { editName, preCheckBox, actionbuttons, pinnedView } = cellRenderers;
  const {
    pinnedHeader,
    scenarioNameHeader,
    dateHeader,
    sharedOnHeader,
    countryHeader,
    brandHeader,
    totalVolumeHeader,
    incrementalVolumeHeader,
    incrementalMaCoHeader,
  } = headerRenderers;

  const columns: ColumnDef<ScenarioType>[] = [
    {
      header: CheckboxHeader,
      accessorKey: 'checkbox',
      cell: preCheckBox,
    },
    {
      header: pinnedHeader,
      accessorKey: 'pinned',
      cell: pinnedView,
    },
    {
      header: scenarioNameHeader || 'Scenario Name',
      accessorKey: 'name',
      cell: editName,
    },
    ...(isShared
      ? [
          {
            header: sharedOnHeader || 'Shared On',
            accessorKey: 'shared_on',
            cell: formatDate,
          },
        ]
      : []),
    {
      header: dateHeader || 'Created On',
      accessorKey: 'date',
      cell: formatDate,
    },

    {
      header: countryHeader || 'Country',
      accessorKey: 'Country',
      cell: TableCell,
    },

    ...(isShared
      ? [
          {
            header: 'Created By',
            accessorKey: 'created_by',
            cell: TableCell,
          },
        ]
      : []),
    {
      header: brandHeader || 'Brand',
      accessorKey: 'brand',
      cell: TableCell,
    },
    {
      header: totalVolumeHeader || 'Total Volume',
      accessorKey: 'incremental_nr',
      cell: TableCell,
    },
    {
      header: incrementalVolumeHeader || 'Incremental Volume',
      accessorKey: 'incremental_volume_%',
      cell: TableCell,
    },
    {
      header: incrementalMaCoHeader || IncMaCoHeader,
      accessorKey: 'incremental_maco',
      cell: TableCell,
    },
    {
      header: 'Actions',
      accessorKey: 'actions',
      cell: actionbuttons,
    },
  ];
  return columns;
};

export const RegionalColumn = (isPTCMode: boolean = false) => [
  C('Districts', 'R_Districts'),
  C('POC Segments', 'R_POC_Segments'),
  ...(isPTCMode
    ? [C('NR per HL (USD)', 'R_NR_Price_per_HL'), C('PTC per HL (USD)', 'R_Price_per_HL')]
    : [C('NR per HL (USD)', 'R_Price_per_HL')]),
  C('Distribution %', 'R_Distribution'),
  C('Launch Period', 'R_Launch_Period'),
];
export const BrazilRegionalColumn = (isPTCMode: boolean = false) => [
  C('Regions', 'R_Regions'),
  C('Channels', 'R_Channels'),
  ...(isPTCMode
    ? [C('NR per HL (USD)', 'R_NR_Price_per_HL'), C('PTC per HL (USD)', 'R_Price_per_HL')]
    : [C('NR per HL (USD)', 'R_Price_per_HL')]),
  C('Distribution %', 'R_Distribution'),
  C('Launch Period', 'R_Launch_Period'),
];

export const UkRegionalColumn = [
  C('Regions', 'R_Regions'),
  C('Retailers', 'R_Markets'),
  C('NR per HL (USD)', 'R_NR_Price_per_HL'),
  C('PTC per HL (USD)', 'R_Price_per_HL'),
  C('Distribution %', 'R_Distribution'),
  C('Launch Period', 'R_Launch_Period'),
];
export const COLRegionalColumn = [
  C('Management', 'R_Regions'),
  C('Channels', 'R_Channels'),
  C('NR per HL (USD)', 'R_NR_Price_per_HL'),
  C('PTC per HL (USD)', 'R_Price_per_HL'),
  C('Distribution %', 'R_Distribution'),
  C('Launch Period', 'R_Launch_Period'),
];

export const MexicoGrowthRateColumn = () => [
  C('Regions', 'R_Regions'),
  C('Channels', 'R_Channels'),
  G('PTC per HL\nGR year1', 'R_price_growth_year1'),
  G('PTC per HL\nGR year2', 'R_price_growth_year2'),
  G('Coverage\nGR year1', 'R_distribution_growth_year1'),
  G('Coverage\nGR year2', 'R_distribution_growth_year2'),
];

export const ZAFGrowthRateColumn = (isPTCMode: boolean = false) => [
  C('Districts', 'R_Districts'),
  C('POC Segments', 'R_POC_Segments'),
  G(isPTCMode ? 'PTC per HL\nGR year1' : 'NR per HL\nGR year1', 'R_price_growth_year1'),
  G(isPTCMode ? 'PTC per HL\nGR year2' : 'NR per HL\nGR year2', 'R_price_growth_year2'),
  G('Distribution\nGR year1', 'R_distribution_growth_year1'),
  G('Distribution\nGR year2', 'R_distribution_growth_year2'),
];

export const BrazilGrowthRateColumn = (isPTCMode: boolean = false) => [
  {
    header: () => (
      <Text
        textAlign="center"
        fontFamily="Avantt"
        fontSize="12px"
        fontWeight="600"
        lineHeight="1.3"
      >
        Regions
      </Text>
    ),
    accessorKey: 'R_Regions',
    cell: (props: { getValue: () => string }) => (
      <Text whiteSpace="normal"> {props.getValue()}</Text>
    ),
  },
  {
    header: () => (
      <Text
        textAlign="center"
        fontFamily="Avantt"
        fontSize="12px"
        fontWeight="600"
        lineHeight="1.3"
      >
        Channels
      </Text>
    ),
    accessorKey: 'R_Channels',
    cell: (props: { getValue: () => string }) => (
      <Text whiteSpace="normal"> {props.getValue()}</Text>
    ),
  },

  {
    header: () => (
      <Text
        whiteSpace="pre-line"
        textAlign="center"
        fontFamily="Avantt"
        fontSize="12px"
        fontWeight="600"
        lineHeight="1.3"
      >
        {isPTCMode ? 'PTC per HL\nGR year1' : 'NR per HL\nGR year1'}
      </Text>
    ),
    accessorKey: 'R_price_growth_year1',
    cell: (props: { getValue: () => string }) => (
      <Text whiteSpace="normal"> {props.getValue()}</Text>
    ),
  },
  {
    header: () => (
      <Text
        whiteSpace="pre-line"
        textAlign="center"
        fontFamily="Avantt"
        fontSize="12px"
        fontWeight="600"
        lineHeight="1.3"
      >
        {isPTCMode ? 'PTC per HL\nGR year2' : 'NR per HL\nGR year2'}
      </Text>
    ),
    accessorKey: 'R_price_growth_year2',
    cell: (props: { getValue: () => string }) => (
      <Text whiteSpace="normal"> {props.getValue()}</Text>
    ),
  },
  {
    header: () => (
      <Text
        whiteSpace="pre-line"
        textAlign="center"
        fontFamily="Avantt"
        fontSize="12px"
        fontWeight="600"
        lineHeight="1.3"
      >
        {'Distribution\nGR year1'}
      </Text>
    ),
    accessorKey: 'R_distribution_growth_year1',
    cell: (props: { getValue: () => string }) => (
      <Text whiteSpace="normal"> {props.getValue()}</Text>
    ),
  },
  {
    header: () => (
      <Text
        whiteSpace="pre-line"
        textAlign="center"
        fontFamily="Avantt"
        fontSize="12px"
        fontWeight="600"
        lineHeight="1.3"
      >
        {'Distribution\nGR year2'}
      </Text>
    ),
    accessorKey: 'R_distribution_growth_year2',
    cell: (props: { getValue: () => string }) => (
      <Text whiteSpace="normal"> {props.getValue()}</Text>
    ),
  },
];

export const UKGrowthRateColumn = () => [
  {
    header: () => (
      <Text
        textAlign="center"
        fontFamily="Avantt"
        fontSize="12px"
        fontWeight="600"
        lineHeight="1.3"
      >
        Regions
      </Text>
    ),
    accessorKey: 'R_Regions',
    cell: (props: { getValue: () => string }) => (
      <Text whiteSpace="normal"> {props.getValue()}</Text>
    ),
  },
  {
    header: () => (
      <Text
        textAlign="center"
        fontFamily="Avantt"
        fontSize="12px"
        fontWeight="600"
        lineHeight="1.3"
      >
        Retailers
      </Text>
    ),
    accessorKey: 'R_Markets',
    cell: (props: { getValue: () => string }) => (
      <Text whiteSpace="normal"> {props.getValue()}</Text>
    ),
  },

  {
    header: () => (
      <Text
        whiteSpace="pre-line"
        textAlign="center"
        fontFamily="Avantt"
        fontSize="12px"
        fontWeight="600"
        lineHeight="1.3"
      >
        {'PTC per HL\nGR year1'}
      </Text>
    ),
    accessorKey: 'R_price_growth_year1',
    cell: (props: { getValue: () => string }) => (
      <Text whiteSpace="normal"> {props.getValue()}</Text>
    ),
  },
  {
    header: () => (
      <Text
        whiteSpace="pre-line"
        textAlign="center"
        fontFamily="Avantt"
        fontSize="12px"
        fontWeight="600"
        lineHeight="1.3"
      >
        {'PTC per HL\nGR year2'}
      </Text>
    ),
    accessorKey: 'R_price_growth_year2',
    cell: (props: { getValue: () => string }) => (
      <Text whiteSpace="normal"> {props.getValue()}</Text>
    ),
  },
  {
    header: () => (
      <Text
        whiteSpace="pre-line"
        textAlign="center"
        fontFamily="Avantt"
        fontSize="12px"
        fontWeight="600"
        lineHeight="1.3"
      >
        {'Distribution\nGR year1'}
      </Text>
    ),
    accessorKey: 'R_distribution_growth_year1',
    cell: (props: { getValue: () => string }) => (
      <Text whiteSpace="normal"> {props.getValue()}</Text>
    ),
  },
  {
    header: () => (
      <Text
        whiteSpace="pre-line"
        textAlign="center"
        fontFamily="Avantt"
        fontSize="12px"
        fontWeight="600"
        lineHeight="1.3"
      >
        {'Distribution\nGR year2'}
      </Text>
    ),
    accessorKey: 'R_distribution_growth_year2',
    cell: (props: { getValue: () => string }) => (
      <Text whiteSpace="normal"> {props.getValue()}</Text>
    ),
  },
];
export const COLGrowthRateColumn = [
  {
    header: () => (
      <Text
        textAlign="center"
        fontFamily="Avantt"
        fontSize="12px"
        fontWeight="600"
        lineHeight="1.3"
      >
        Management
      </Text>
    ),
    accessorKey: 'R_Regions',
    cell: (props: { getValue: () => string }) => (
      <Text whiteSpace="normal"> {props.getValue()}</Text>
    ),
  },
  {
    header: () => (
      <Text
        textAlign="center"
        fontFamily="Avantt"
        fontSize="12px"
        fontWeight="600"
        lineHeight="1.3"
      >
        Channels
      </Text>
    ),
    accessorKey: 'R_Channels',
    cell: (props: { getValue: () => string }) => (
      <Text whiteSpace="normal"> {props.getValue()}</Text>
    ),
  },

  {
    header: () => (
      <Text
        whiteSpace="pre-line"
        textAlign="center"
        fontFamily="Avantt"
        fontSize="12px"
        fontWeight="600"
        lineHeight="1.3"
      >
        {'PTC per HL\nGR year1'}
      </Text>
    ),
    accessorKey: 'R_price_growth_year1',
    cell: (props: { getValue: () => string }) => (
      <Text whiteSpace="normal"> {props.getValue()}</Text>
    ),
  },
  {
    header: () => (
      <Text
        whiteSpace="pre-line"
        textAlign="center"
        fontFamily="Avantt"
        fontSize="12px"
        fontWeight="600"
        lineHeight="1.3"
      >
        {'PTC per HL\nGR year2'}
      </Text>
    ),
    accessorKey: 'R_price_growth_year2',
    cell: (props: { getValue: () => string }) => (
      <Text whiteSpace="normal"> {props.getValue()}</Text>
    ),
  },
  {
    header: () => (
      <Text
        whiteSpace="pre-line"
        textAlign="center"
        fontFamily="Avantt"
        fontSize="12px"
        fontWeight="600"
        lineHeight="1.3"
      >
        {'Distribution\nGR year1'}
      </Text>
    ),
    accessorKey: 'R_distribution_growth_year1',
    cell: (props: { getValue: () => string }) => (
      <Text whiteSpace="normal"> {props.getValue()}</Text>
    ),
  },
  {
    header: () => (
      <Text
        whiteSpace="pre-line"
        textAlign="center"
        fontFamily="Avantt"
        fontSize="12px"
        fontWeight="600"
        lineHeight="1.3"
      >
        {'Distribution\nGR year2'}
      </Text>
    ),
    accessorKey: 'R_distribution_growth_year2',
    cell: (props: { getValue: () => string }) => (
      <Text whiteSpace="normal"> {props.getValue()}</Text>
    ),
  },
];

// Update the MexicoRegionalColumn to exclude growth rate columns
export const MexicoRegionalColumn = (isPTCMode: boolean = false) => [
  {
    header: 'Regions',
    accessorKey: 'R_Regions',
    cell: (props: { getValue: () => string }) => (
      <Text whiteSpace="normal"> {props.getValue()}</Text>
    ),
  },
  {
    header: 'Channels',
    accessorKey: 'R_Channels',
    cell: (props: { getValue: () => string }) => (
      <Text whiteSpace="normal"> {props.getValue()}</Text>
    ),
  },
  ...(isPTCMode
    ? [
        {
          header: 'NR per HL (USD)',
          accessorKey: 'R_NR_Price_per_HL',
          cell: (props: { getValue: () => string }) => (
            <Text whiteSpace="normal"> {props.getValue()}</Text>
          ),
        },
        {
          header: 'PTC per HL (USD)',
          accessorKey: 'R_Price_per_HL',
          cell: (props: { getValue: () => string }) => (
            <Text whiteSpace="normal"> {props.getValue()}</Text>
          ),
        },
      ]
    : [
        {
          header: 'PTC per HL (USD)',
          accessorKey: 'R_Price_per_HL',
          cell: (props: { getValue: () => string }) => (
            <Text whiteSpace="normal"> {props.getValue()}</Text>
          ),
        },
      ]),
  {
    header: 'Coverage %',
    accessorKey: 'R_Distribution',
    cell: (props: { getValue: () => string }) => (
      <Text whiteSpace="normal"> {props.getValue()}</Text>
    ),
  },
  {
    header: 'Launch Period',
    accessorKey: 'R_Launch_Period',
    cell: (props: { getValue: () => string }) => (
      <Text whiteSpace="normal"> {props.getValue()}</Text>
    ),
  },
];

// China Regional Column - CHN always shows PTC, CNN follows isPTCMode

// China Regional Column - same structure as Mexico
export const ChinaRegionalColumn = () => [
  {
    header: 'Regions',
    accessorKey: 'R_Regions',
    cell: (props: { getValue: () => string }) => (
      <Text whiteSpace="normal"> {props.getValue()}</Text>
    ),
  },
  {
    header: 'Channels',
    accessorKey: 'R_Channels',
    cell: (props: { getValue: () => string }) => (
      <Text whiteSpace="normal"> {props.getValue()}</Text>
    ),
  },
  {
    header: 'NR per HL (USD)',
    accessorKey: 'R_NR_Price_per_HL',
    cell: (props: { getValue: () => string }) => (
      <Text whiteSpace="normal"> {props.getValue()}</Text>
    ),
  },
  {
    header: 'PTC per Unit (RMB)',
    accessorKey: 'R_Price_per_HL',
    cell: (props: { getValue: () => string }) => (
      <Text whiteSpace="normal"> {props.getValue()}</Text>
    ),
  },
  {
    header: 'Nielsen Weighted distribution',
    accessorKey: 'R_Distribution',
    cell: (props: { getValue: () => string }) => (
      <Text whiteSpace="normal"> {props.getValue()}</Text>
    ),
  },
  {
    header: 'Launch Period',
    accessorKey: 'R_Launch_Period',
    cell: (props: { getValue: () => string }) => (
      <Text whiteSpace="normal"> {props.getValue()}</Text>
    ),
  },
];
export const ChinaRegionalColumn2 = () => [
  {
    header: 'Regions',
    accessorKey: 'R_Regions',
    cell: (props: { getValue: () => string }) => (
      <Text whiteSpace="normal"> {props.getValue()}</Text>
    ),
  },
  {
    header: 'Channels',
    accessorKey: 'R_Channels',
    cell: (props: { getValue: () => string }) => (
      <Text whiteSpace="normal"> {props.getValue()}</Text>
    ),
  },
  {
    header: 'NR per HL (USD)',
    accessorKey: 'R_Price_per_HL',
    cell: (props: { getValue: () => string }) => (
      <Text whiteSpace="normal"> {props.getValue()}</Text>
    ),
  },

  {
    header: '% of Wholesalers coverage (STW data)',
    accessorKey: 'R_Distribution',
    cell: (props: { getValue: () => string }) => (
      <Text whiteSpace="normal"> {props.getValue()}</Text>
    ),
  },
  {
    header: 'Launch Period',
    accessorKey: 'R_Launch_Period',
    cell: (props: { getValue: () => string }) => (
      <Text whiteSpace="normal"> {props.getValue()}</Text>
    ),
  },
];

// China Growth Rate Column - Always shows PTC headers for both CHN and CNN
export const ChinaGrowthRateColumn = () => [
  C('Regions', 'R_Regions'),
  C('Channels', 'R_Channels'),
  G('PTC per Unit\nGR year1', 'R_price_growth_year1'),
  G('PTC per Unit\nGR year2', 'R_price_growth_year2'),
  G('Nielsen Weighted\ndistribution GR year1', 'R_distribution_growth_year1'),
  G('Nielsen Weighted\ndistribution GR year2', 'R_distribution_growth_year2'),
];
export const ChinaGrowthRateColumn2 = () => [
  C('Regions', 'R_Regions'),
  C('Channels', 'R_Channels'),
  G('NR per HL\nGR year1', 'R_price_growth_year1'),
  G('NR per HL\nGR year2', 'R_price_growth_year2'),
  G('% of wholesalers coverage\n(STW data) year1', 'R_distribution_growth_year1'),
  G('% of wholesalers coverage\n(STW data) year2', 'R_distribution_growth_year2'),
];

export const InnoColumns = (
  isSwitchOn: boolean,
  country: string,
  isSegmentLevel: boolean = false,
  selectedAggregate?: string
) => {
  // Helper function to format numbers with commas
  const formatNumberWithCommas = (value: string | number | undefined): string => {
    if (value === undefined || value === null || value === '') return 'N/A';
    const numValue = typeof value === 'string' ? Number.parseFloat(value) : value;
    if (Number.isNaN(numValue)) return 'N/A';
    return numValue.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
  };

  const columns = [
    {
      header: isSegmentLevel ? 'Segment' : 'Brand',
      accessorKey: 'brand',
      cell: (props: { getValue: () => string }) => (
        <Text whiteSpace="normal" fontFamily="Avantt">
          {props.getValue()}
        </Text>
      ),
    },
    {
      header: 'Pack Type',
      accessorKey: 'packType',
      cell: (props: { getValue: () => string }) => (
        <Text whiteSpace="normal" fontFamily="Avantt">
          {props.getValue()}
        </Text>
      ),
    },
    {
      header: country === 'USA' ? 'Pack Size (oz)' : 'Pack Size (ml)',
      accessorKey: 'packSize',
      cell: (props: { getValue: () => string }) => (
        <Text whiteSpace="normal" fontFamily="Avantt">
          {props.getValue()}
        </Text>
      ),
    },
    {
      header: isSwitchOn ? 'Volume (HL)' : 'Volume %',
      accessorKey: isSwitchOn ? 'volume_hl' : 'volume_share',
      cell: (props: { row: { original: ModalType } }) => {
        const { volume_hl, volume_share, volume_hl_delta, volume_share_delta } = props.row.original;
        const value = isSwitchOn ? volume_hl : volume_share;

        let displayValue: string;
        if (isSwitchOn) {
          displayValue = formatNumberWithCommas(value);
        } else {
          displayValue = value ? `${value}%` : 'N/A';
        }

        const delta = isSwitchOn ? volume_hl_delta : volume_share_delta;
        const deltaNum = delta ? Number.parseFloat(delta) : 0;
        const isPositive = deltaNum >= 0;
        const sign = isPositive ? '+' : '';
        const unit = isSwitchOn ? '%' : ' pp';

        return (
          <Flex direction="column" alignItems="center">
            <Text whiteSpace="normal" fontFamily="Avantt">
              {displayValue || 'N/A'}
            </Text>
            {delta && (
              <Text
                whiteSpace="normal"
                fontFamily="Avantt"
                fontSize="8px"
                color={isPositive ? 'green.500' : 'red.500'}
              >
                ({sign}
                {deltaNum}
                {unit} {isPositive ? '▲' : '▼'})
              </Text>
            )}
          </Flex>
        );
      },
    },
    {
      header: isSwitchOn ? 'Volume %' : 'Volume (HL)',
      accessorKey: isSwitchOn ? 'volume_share' : 'volume_hl',
      cell: (props: { row: { original: ModalType } }) => {
        const { volume_hl, volume_share, volume_hl_delta, volume_share_delta } = props.row.original;
        const value = isSwitchOn ? volume_share : volume_hl;

        let displayValue: string;
        if (isSwitchOn) {
          displayValue = value ? `${value}%` : 'N/A';
        } else {
          displayValue = formatNumberWithCommas(value);
        }

        const delta = isSwitchOn ? volume_share_delta : volume_hl_delta;
        const deltaNum = delta ? Number.parseFloat(delta) : 0;
        const isPositive = deltaNum >= 0;
        const sign = isPositive ? '+' : '';
        const unit = isSwitchOn ? ' pp' : '%';

        return (
          <Flex direction="column" alignItems="center">
            <Text whiteSpace="normal" fontFamily="Avantt">
              {displayValue || 'N/A'}
            </Text>
            {delta && (
              <Text
                whiteSpace="normal"
                fontFamily="Avantt"
                fontSize="8px"
                color={isPositive ? 'green.500' : 'red.500'}
              >
                ({sign}
                {deltaNum}
                {unit} {isPositive ? '▲' : '▼'})
              </Text>
            )}
          </Flex>
        );
      },
    },
  ];

  // Conditionally add the "Numeric Distribution %" column when selectedAggregate is "numeric_distribution"
  if (selectedAggregate === 'numeric_distribution') {
    columns.push({
      header: 'Numeric Distribution %',
      accessorKey: 'distribution',
      cell: (props: { row: { original: ModalType } }) => {
        const { distribution, distribution_delta } = props.row.original;
        const displayValue = distribution ? `${distribution}%` : 'N/A';
        const deltaNum = distribution_delta ? Number.parseFloat(distribution_delta) : 0;
        const isPositive = deltaNum >= 0;
        const sign = isPositive ? '+' : '';

        return (
          <Flex direction="column" alignItems="center">
            <Text whiteSpace="normal" fontFamily="Avantt">
              {displayValue}
            </Text>
            {distribution_delta && (
              <Text
                whiteSpace="normal"
                fontFamily="Avantt"
                fontSize="8px"
                color={isPositive ? 'green.500' : 'red.500'}
              >
                ({sign}
                {deltaNum} pp {isPositive ? '▲' : '▼'})
              </Text>
            )}
          </Flex>
        );
      },
    });
  }

  // Conditionally add the "Pack Quantity" column only for specific countries AND not segment level
  if ((country === 'UK' || country === 'USA' || country === 'CHN') && !isSegmentLevel) {
    columns.splice(3, 0, {
      header: 'Pack Quantity',
      accessorKey: 'pack_quantity',
      cell: (props: { getValue: () => string }) => (
        <Text whiteSpace="normal" fontFamily="Avantt">
          {props.getValue()}
        </Text>
      ),
    });
  }

  return columns;
};

export const tooltipContent: Record<string, Record<string, Record<string, SectionInfo[]>>> = {
  // GROUP 2 (EUR - UK)
  UK: {
    all: {
      Entry: [
        {
          packs: [
            { quantity: '1', size: '≤300 ml' },
            { quantity: '2-8', size: 'All' },
          ],
        },
      ],
      Frequency: [
        {
          packs: [
            { quantity: '1', type: 'Bottles', size: '301-600 ml' },
            { quantity: '1', type: 'Cans', size: '301-400 ml' },
            { quantity: '9-15', size: 'All' },
          ],
        },
      ],
      Intensity: [
        {
          packs: [
            { quantity: '1', type: 'Bottles', size: '>600 ml' },
            { quantity: '1', type: 'Cans', size: '>400 ml' },
            { quantity: '>15', size: 'All' },
          ],
        },
      ],
    },
  },
  // GROUP 3 (NAZ - US)
  USA: {
    all: {
      Entry: [
        {
          packs: [
            { quantity: '1-3', size: '≤16oz' },
            { quantity: '4-6', size: 'All Sizes' },
          ],
        },
      ],
      Frequency: [
        {
          packs: [
            { quantity: '1-3', size: '16-25oz' },
            { quantity: '8-20', size: 'All Sizes' },
          ],
        },
      ],
      Intensity: [
        {
          packs: [
            { quantity: '1-3', size: '>25oz' },
            { quantity: '>24', size: 'All Sizes' },
          ],
        },
      ],
    },
  },
};

export const months = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

export const STORAGE_KEYS = {
  EXPANDED_KEYS: 'explore_expanded_keys',
  IS_TOTAL_MARKET_COLLAPSED: 'explore_is_total_market_collapsed',
  FILTERED_DATA_STRUCTURE: 'explore_filtered_data_structure',
  FILTERED_DATA: 'explore_filtered_data',
  MARKET_STRUCTURE: 'explore_market_structure',
  COUNTRY: 'explore_current_country',
  // Pack Assessment specific keys
  PACK_EXPANDED_SEGMENTS: 'pack_expanded_segments',
  PACK_EXPANDED_MANUFACTURERS: 'pack_expanded_manufacturers',
  PACK_TOTAL_MARKET_EXPANDED: 'pack_total_market_expanded',
  PACK_COUNTRY: 'pack_current_country',
};

export const COUNTRY_CODE_MAP: Record<string, string> = {
  // Legacy display names → API country code (lowercase keys from /api/users/approved)
  'South Africa': 'zaf',
  'South Africa (SA)': 'zaf',
  Brazil: 'bra',
  'Brazil (BR)': 'bra',
  UK: 'uk',
  'Uk-Brew': 'uk',
  'UK-Brew': 'uk',
  Mexico: 'mex',
  'Mex-Brew': 'mex',
  MEX: 'mex',
  Colombia: 'col',
  Korea: 'kor',
  Argentina: 'arg',
  China: 'chn',
  China2: 'cnn',
  Peru: 'per',
  Ecuador: 'ecu',
  // Identity mappings — for simulations already storing the API key directly
  zaf: 'zaf',
  bra: 'bra',
  uk: 'uk',
  mex: 'mex',
  col: 'col',
  peru: 'per',
  kor: 'kor',
  arg: 'arg',
  chn: 'chn',
  cnn: 'cnn',
  per: 'per',
  ecu: 'ecu',
};
